import { createSlice } from '@reduxjs/toolkit';
import { faker } from '@faker-js/faker';
import * as Sentry from '@sentry/react';

import { addresses, FAKER_LOCAL, cardImg } from 'common_modules/consts';
import { setUserFormLocale } from 'common_modules/utils/setUserFormLocale';
import { setUserFormState } from 'common_modules/utils/setUserFormState';
import { setUserFormLocaleCZ } from 'common_modules/utils/setUserFormLocaleCZ';

import { selectors } from './selectors';
import { thunks } from './thunks';

import en_vocab from './vocab/en';
import ca_vocab from './vocab/ca';
import fr_vocab from './vocab/fr';
import it_vocab from './vocab/it';
import es_vocab from './vocab/es';
import de_vocab from './vocab/de';
import pt_vocab from './vocab/pt';
import fi_vocab from './vocab/fi';
import pl_vocab from './vocab/pl';
import ch_vocab from './vocab/ch';
import dk_vocab from './vocab/dk';
import se_vocab from './vocab/se';
import be_vocab from './vocab/be';
import no_vocab from './vocab/no';
import at_vocab from './vocab/at';
import nz_vocab from './vocab/nz';
import au_vocab from './vocab/au';
import sa_vocab from './vocab/sa';
import cz_vocab from './vocab/cz';

const initialState = {
  baseUrl: 'https://trustcheckout.net/api/offer_3ds',
  // baseUrl: 'http://192.168.1.104/api/offer_3ds',
  redirectUrl: 'https://trustcheckout.net/api/redirected',
  saveClickUrl: 'https://trustcheckout.net/api/save-click',
  // saveClickUrl: 'http://192.168.1.104/api/save-click',
  sendClickUrl: 'https://trustcheckout.net/api/send-click',
  checkCardUrl: 'https://trustcheckout.net/api/check_card',
  // checkCardUrl: 'http://192.168.1.104/api/check_card',
  updateClickUrl: 'https://trustcheckout.net/api/update-click',
  getMerchantUrl: 'https://trustcheckout.net/api/get_merchant',
  // getMerchantUrl: 'http://192.168.1.102/api/get_merchant',
  getMerchantUrlForCountry:
    'https://trustcheckout.net/api/get_merchant_for_country',
  // getMerchantUrlForCountry: 'http://192.168.1.102/api/get_merchant_for_country',
  baseUrl3DS: 'https://api.3dsintegrator.com/v2.2/',
  // baseUrl3DS: 'https://api-sandbox.3dsintegrator.com/v2.2/',
  vocab: {
    en: en_vocab,
    ca: ca_vocab,
    fr: fr_vocab,
    it: it_vocab,
    es: es_vocab,
    de: de_vocab,
    pt: pt_vocab,
    fi: fi_vocab,
    pl: pl_vocab,
    ch: ch_vocab,
    dk: dk_vocab,
    se: se_vocab,
    be: be_vocab,
    no: no_vocab,
    at: at_vocab,
    nz: nz_vocab,
    au: au_vocab,
    sa: sa_vocab,
    cz: cz_vocab,
  },
  locale: 'en',
  navigatorLocale: 'en',
  multiLanguages: [
    'fr',
    'en',
    'es',
    'it',
    'de',
    'pl',
    'pt',
    'fi',
    'au',
    'at',
    'ch',
    'dk',
    'se',
    'be',
    'no',
    'nz',
    'sa',
    'cz',
  ],
  userForm: {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    city: '',
    address: '',
    zip: '',
    state: '',
  },
  paymentForm: {
    cardNumber: '',
    expirationMonth: '',
    expirationYear: '',
    name: '',
    cvv: '',
    cardType: 'unknown',
  },
  clickData: {
    lp: null,
    p: null,
    pub: null,
    s: null,
    r: null,
  },
  showLoader: false,
  showPopup: false,
  popupMsg: '',
  cardImg,
  successPayment: false,
  ...addresses,
};

const setUserFormLocaleFaker = (state) => {
  // state.userForm.city = faker.address.city();
  // state.userForm.zip = faker.address.zipCode();
  // state.userForm.address = faker.address.streetAddress();
  const stateFaker = faker.address.stateAbbr();
  if (stateFaker !== undefined) {
    state.userForm.state = stateFaker;
  } else {
    setUserFormState(state, state.locale);
  }
  console.log('state: ' + state.userForm.state);
};

const state = createSlice({
  name: 'state',
  initialState,
  reducers: {
    setLocale(state, { payload }) {
      state.locale = payload;
    },
    setNavigatorLocale(state, { payload }) {
      const { navigatorLang, htmlLang } = payload;

      if (navigatorLang && state.multiLanguages.includes(navigatorLang)) {
        state.navigatorLocale = navigatorLang;
      } else {
        state.navigatorLocale = htmlLang;
      }
    },
    setUserFormData(state, { payload }) {
      state.userForm[payload.key] = payload.value;
    },
    setPaymentFormData(state, { payload }) {
      state.paymentForm = {
        ...state.paymentForm,
        ...payload,
      };
    },
    updatePaymentFormDataField(state, { payload }) {
      state.paymentForm[payload.key] = payload.value;
    },
    setClickData(state, { payload }) {
      state.clickData[payload.key] = payload.value;
    },
    showPopupHandler(state, { payload }) {
      state.showPopup = true;
      state.popupMsg = payload;
    },
    closePopupHandler(state) {
      state.showPopup = false;
      state.popupMsg = '';
    },
    showLoaderHandler(state) {
      state.showLoader = true;
    },
    hideLoaderHandler(state) {
      state.showLoader = false;
    },
    setCartType(state, { payload }) {
      switch (true) {
        case /^5.+$/.test(payload.value):
          state.paymentForm.cardType = 'mastercard';
          break;
        case /^[4|7].+$/.test(payload.value):
          state.paymentForm.cardType = 'visa';
          break;
        default:
          state.paymentForm.cardType = 'unknown';
      }
    },
    setSuccessPayment(state, { payload }) {
      window.localStorage.setItem('data_token', 'sdfffgdsgdgfdd');
      if (payload !== undefined) {
        window.localStorage.setItem(
          'unsubscribe_url',
          `http://${payload.descriptor_url}`
        );
        window.localStorage.setItem('product_site', payload.product_site);
        window.localStorage.setItem('phone', payload.support_phone);
        window.localStorage.setItem('email', payload.support_email);
      }
      state.successPayment = true;
      Sentry.captureMessage('Payment success');
    },
    getFakerLocal: (state) => {
      try {
        const fakerLocale = FAKER_LOCAL[state.locale] || state.locale;
        faker.setLocale(fakerLocale);
        setUserFormLocaleFaker(state);
        if (state.locale === 'pt') setUserFormLocale(state, state.locale);
        if (state.locale === 'cz') setUserFormLocaleCZ(state, state.locale);
      } catch (err) {
        console.log('faker=>', err);
        setUserFormLocale(state, state.locale);
      }
    },
  },
});

export const globalState = {
  actions: state.actions,
  selectors,
  thunks,
};

export default state.reducer;
